import React, { useState, useEffect } from "react";
import { Checkbox, Input, Select, Button, Modal, Table, Popconfirm, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  saveSpecialAccess,
  fetchRubricsByFormId,
  fetchOrganizationsByUserId,
  fetchSpecialAccessRecords,
  disableSpecialAccessRecord,
  enableSpecialAccessRecord
} from "../action_creators";
import { auth } from "../../Auth";
import { generateProjectShell } from "../../ProjectBuilder/utilities";
import { setAllowInspectorToCreateProjects, setAllowSpecialRegistrationForm } from "../reducer";

const { Option } = Select;

const AccessSettings = () => {
  const dispatch = useDispatch();
  const state = useSelector((state)=> state);
  const rubrics = useSelector((state) => state.rubrics.rubrics || []);
  const clients = useSelector((state) => state.clients.clients || []);
  const form = useSelector((state) => state.form || {});
  const formId = form.id;
  const specialAccessRecords = useSelector((state) => state.specialAccess.data || []);
  const [allowSpecialRegistration, setAllowSpecialRegistration] = useState(form.allowSpecialRegistration || false);
  const [allowInspector, setAllowInspector] = useState(form.allowInspectorToCreateProjects || false);
  const [specialUrl, setSpecialUrl] = useState("");
  const [selectedRubric, setSelectedRubric] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (formId) {
      dispatch(fetchRubricsByFormId(formId));
      dispatch(fetchOrganizationsByUserId());
      dispatch(fetchSpecialAccessRecords(formId));
    }
  }, [dispatch, formId, allowSpecialRegistration]);


  useEffect(() => {
    if (allowSpecialRegistration && formId) {
      setSpecialUrl(`${generateUniqueSuffix()}`);
    } else {
      setSpecialUrl("");
    }
  }, [allowSpecialRegistration, formId]);

  const generateUniqueSuffix = () => {
    const timestamp = Date.now();
    const randomChar = String.fromCharCode(65 + Math.floor(Math.random() * 26));
    return `${timestamp}${randomChar}`;
  };

  const handleAllowSpecialRegistrationChange = (e) => {
    const isChecked = e.target.checked;
    setAllowSpecialRegistration(isChecked);
    dispatch(setAllowSpecialRegistrationForm(isChecked)); // Dispatch Redux action
  };
  const handleAllowInspectorToCreateProjectsChange = (e) => {
    const isChecked = e.target.checked;
    setAllowInspector(isChecked);

    dispatch(setAllowInspectorToCreateProjects(isChecked));
  };

  const handleRubricChange = (value) => {
    setSelectedRubric(value);
  };

  const handleClientChange = (value) => {
    setSelectedClient(value);
  };

  const handleSave = async () => {
    if (!selectedRubric || !selectedClient) {
      Modal.error({
        title: "Missing Required Fields",
        content: "Please select both a Rubric and a Client.",
      });
      return;
    }
    setLoading(true);
    const rubric = rubrics.find((r) => r.id === selectedRubric);
    const client = clients.find((c) => c.id === selectedClient);
    const specialAccessData = {
      specialurl: specialUrl,
      formid: formId,
      rubricname: rubric.name,
      rubricid: rubric.id,
      formdetails: await generateProjectShell(form) || "",
      rubricdetails: rubric || "", // Assuming rubric has a details field
      clientname: client.name,
      clientid: client.id,
      clientdetails: client || "", // Assuming client has a details field
      usercreated: auth.sunkaizenUser.id,
      datecreated: new Date(),
      status: "new",
    };

    try {
      const result = await dispatch(saveSpecialAccess(specialAccessData));

      if (result && result.type === "SAVE_SPECIAL_ACCESS_SUCCESS") {
        resetFields();
        dispatch(fetchSpecialAccessRecords(formId)); // Fetch updated records after saving
        setIsModalVisible(false); // Close the modal
      }
    } catch (error) {
      console.error("Error saving special access data:", error);
    }
    setLoading(false);
  };

  const resetFields = () => {
    setSpecialUrl(`${generateUniqueSuffix()}`);
    setSelectedRubric("");
    setSelectedClient("");
  };

  const handleDeleteRecord = async (recordId) => {
    try {
      await dispatch(disableSpecialAccessRecord(recordId));
      dispatch(fetchSpecialAccessRecords(formId));
    } catch (error) {
      console.error("Error disabling special access record: ", error);
    }
  };
  const handleEnableRecord = async (recordId) => {
    try {
      await dispatch(enableSpecialAccessRecord(recordId));
      dispatch(fetchSpecialAccessRecords(formId));
    } catch (error) {
      console.error("Error enabling special access record: ", error);
    }
  };

  const columns = [
    {
      title: "Client Name",
      dataIndex: "clientname",
      key: "clientname",
    },
    {
      title: "Rubric Name",
      dataIndex: "rubricname",
      key: "rubricname",
    },
    {
      title: "Special URL",
      dataIndex: "specialurl",
      key: "specialurl",
    },
    {
      title: "Date Created",
      dataIndex: "datecreated",
      key: "datecreated",
      render: (text) => new Date(text.seconds * 1000).toLocaleString(),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (status === "new" ? "Invited" : status),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <>
         {record.status === 'new' &&(<Popconfirm
          title="Are you sure you want to disable this record?"
          onConfirm={() => handleDeleteRecord(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="link">Delete</Button>
        </Popconfirm>)}
        {record.status === 'disabled' && (
          <Popconfirm
            title="Are you sure you want to enable this record?"
            onConfirm={() => handleEnableRecord(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link">Enable</Button>
          </Popconfirm>
        )}
        </>
       
      ),
    },
  ];

  return (
    <div>
      <h3>Access Settings</h3>
      <Checkbox checked={allowSpecialRegistration} onChange={handleAllowSpecialRegistrationChange}>
        Allow Projects with this form to be created during special registration
      </Checkbox>
      <br/>
      <Checkbox
        checked={allowInspector}
        onChange={handleAllowInspectorToCreateProjectsChange}
      >
        Allow Inspector to Create Additional Projects for this Form
      </Checkbox>
      <br/>
      {allowSpecialRegistration && (
        <>
          <div style={{ marginTop: "1rem" }}>
            <Tooltip title="The Special URL should be accessed as: hostname/invite/value-of-Special-URL">
              <p style={{ marginBottom: "1rem" }}>
                <em>Example: {window.location.origin}/invite/{specialUrl}</em>
              </p>
            </Tooltip>
            <Table
              columns={columns}
              dataSource={specialAccessRecords}
              rowKey="id"
              pagination={false}
              style={{
                marginBottom: "1rem",
                backgroundColor: "white",
                border: "1px solid #f0f0f0",
              }}
            />
            <Button type="primary" onClick={() => setIsModalVisible(true)}>
              Add New Record
            </Button>
            <Modal
              title="Add Special Access Record"
              visible={isModalVisible}
              onOk={handleSave}
              onCancel={() => setIsModalVisible(false)}
              confirmLoading = {loading}
            >
              <div>
                <label>
                  Special URL: (<em>{window.location.origin}/invite/</em>)
                </label>
                <Input value={specialUrl} readOnly style={{ marginBottom: "1rem" }} />
                <label>Rubrics:</label>
                <Select
                  style={{ width: "100%", marginBottom: "1rem" }}
                  onChange={handleRubricChange}
                  value={selectedRubric}
                >
                  {rubrics.map((rubric) => (
                    <Option key={rubric.id} value={rubric.id}>
                      {rubric.name}
                    </Option>
                  ))}
                </Select>
                <label>Clients:</label>
                <Select
                  style={{ width: "100%", marginBottom: "1rem" }}
                  onChange={handleClientChange}
                  value={selectedClient}
                >
                  {clients.map((client) => (
                    <Option key={client.id} value={client.id}>
                      {client.name}
                    </Option>
                  ))}
                </Select>
  
              </div>
            </Modal>
          </div>
        </>
      )}
    </div>
  );
};

export default AccessSettings;
